import { useEffect, useRef, useMemo } from 'react';
import { Controller } from 'react-hook-form';
import classNames from 'classnames';
import { AlurkerjaInputType } from '../../types/InputType';

interface RenderCodeProps {
  props: AlurkerjaInputType;
}

type FormElement = HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;

// Handle input element value
function readInputValue(el: HTMLInputElement) {
  const type = (el.getAttribute("type") || "text").toLowerCase();
  
  if (type === "checkbox") {
    if (!el.checked) return null;
    return el.value === "on" ? true : el.value;
  }
  
  if (type === "radio") {
    return el.checked ? el.value : null;
  }
  
  if (type === "number") {
    return el.value === "" ? null : Number(el.value);
  }
  
  if (type === "file") {
    return el.files && el.files.length ? el.files[0] : null;
  }
  
  return el.value;
}

// Handle select element value
function readSelectValue(el: HTMLSelectElement) {
  return el.multiple ? Array.from(el.selectedOptions).map(o => o.value) : el.value;
}

// Utility functions untuk membaca nilai elemen form
function readElementValue(el: FormElement) {
  if (el instanceof HTMLInputElement) {
    return readInputValue(el);
  }
  
  if (el instanceof HTMLSelectElement) {
    return readSelectValue(el);
  }
  
  return el.value;
}

// Merge field values (handle arrays untuk checkbox/multi-select)
function mergeFieldValue(acc: Record<string, any>, name: string, value: any, el: Element) {
  if (value === null) return acc;
  const isArrayName = name.endsWith('[]') || (el instanceof HTMLSelectElement && el.multiple);
  if (isArrayName) {
    const base = name.replace(/\[\]$/, "");
    const current = acc[base] ?? [];
    const v = value === true ? "on" : value;
    return { ...acc, [base]: Array.isArray(v) ? v : [...current, v] };
  }
  acc[name] = value;
  return acc;
}

// Extract semua nilai dari form
function extractAllValues(root: HTMLElement) {
  const fields = root.querySelectorAll<FormElement>(
    "input[name]:not([disabled]), select[name]:not([disabled]), textarea[name]:not([disabled])"
  );
  let data: Record<string, any> = {};
  for (const el of fields) {
    const name = el.getAttribute("name")!;
    const v = readElementValue(el);
    data = mergeFieldValue(data, name, v, el);
  }
  return data;
}

// Sync semua field ke React Hook Form
function syncAllFieldsToRHF(root: HTMLElement, setValue: (n: string, v: any) => void) {
  const data = extractAllValues(root);
  for (const [k, v] of Object.entries(data)) {
    setValue(k, v);
  }
}

// Sync data dari React Hook Form ke HTML elements
function updateInputElement(el: HTMLInputElement, value: any, isArrayName: boolean) {
  const type = el.type.toLowerCase();
  
  if (type === "checkbox") {
    if (isArrayName && Array.isArray(value)) {
      el.checked = value.includes(el.value);
    } else {
      el.checked = Boolean(value);
    }
  } else if (type === "radio") {
    el.checked = el.value === value;
  } else if (type === "file") {
    // File inputs tidak bisa diset secara programatis
    return;
  } else {
    el.value = String(value || '');
  }
}

function updateSelectElement(el: HTMLSelectElement, value: any) {
  if (el.multiple && Array.isArray(value)) {
    for (const option of el.options) {
      option.selected = value.includes(option.value);
    }
  } else {
    el.value = String(value || '');
  }
}

function syncRHFToAllFields(root: HTMLElement, formData: Record<string, any>) {
  const fields = root.querySelectorAll<FormElement>(
    "input[name]:not([disabled]), select[name]:not([disabled]), textarea[name]:not([disabled])"
  );
  
  for (const el of fields) {
    const name = el.getAttribute("name")!;
    const isArrayName = name.endsWith('[]');
    const key = isArrayName ? name.replace(/\[\]$/, "") : name;
    const value = formData[key];
    
    if (value === undefined) continue;
    
    if (el instanceof HTMLInputElement) {
      updateInputElement(el, value, isArrayName);
    } else if (el instanceof HTMLSelectElement) {
      updateSelectElement(el, value);
    } else if (el instanceof HTMLTextAreaElement) {
      el.value = String(value || '');
    }
  }
}

export const RenderCode = ({ props }: RenderCodeProps) => {
  const { item, form: { control, setValue, getValues, watch } } = props;
  
  const containerRef = useRef<HTMLDivElement | null>(null);
  const htmlContent = item?.edit_tabs?.content?.html_content || '';

  // Memoize HTML untuk performa
  const memoizedHtml = useMemo(() => ({ __html: htmlContent }), [htmlContent]);

  // Watch semua form values untuk sync ke HTML
  const watchedValues = watch();

  // Setup event delegation untuk sync dengan React Hook Form
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // Sinkronisasi awal
    syncAllFieldsToRHF(container, setValue);

    const onFormChange = (evt: Event) => {
      const namedEl = (evt.target as Element)?.closest<FormElement>("[name]");
      if (!namedEl || namedEl.hasAttribute("disabled")) return;

      const name = namedEl.getAttribute("name")!;
      const isArrayName = name.endsWith('[]') || (namedEl instanceof HTMLSelectElement && namedEl.multiple);
      const key = isArrayName ? name.replace(/\[\]$/, "") : name;

      // Handle berbagai tipe input
      if (namedEl instanceof HTMLInputElement && namedEl.type === "checkbox") {
        const checkboxes = container.querySelectorAll<HTMLInputElement>(`input[type="checkbox"][name="${name}"]`);
        const arr = Array.from(checkboxes).filter(c => c.checked).map(c => (c.value === "on" ? true : c.value));
        setValue(key, arr);
      } else if (namedEl instanceof HTMLInputElement && namedEl.type === "radio") {
        const checked = container.querySelector<HTMLInputElement>(`input[type="radio"][name="${name}"]:checked`);
        setValue(name, checked ? checked.value : null);
      } else {
        const val = readElementValue(namedEl);
        setValue(key, val);
      }
    };

    // Event listeners untuk berbagai perubahan form
    container.addEventListener("input", onFormChange, true);
    container.addEventListener("change", onFormChange, true);
    container.addEventListener("blur", onFormChange, true);

    return () => {
      container.removeEventListener("input", onFormChange, true);
      container.removeEventListener("change", onFormChange, true);
      container.removeEventListener("blur", onFormChange, true);
    };
  }, [setValue, htmlContent]);

  // Sync perubahan dari luar ke HTML elements
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // Sync semua watched values ke HTML elements
    syncRHFToAllFields(container, watchedValues);
  }, [watchedValues]);

  // Set nilai awal ke form
  useEffect(() => {
    if (htmlContent) {
      setValue(item.name, htmlContent);
    }
  }, [htmlContent, item.name, setValue]);

  return (
    <div>
      <Controller
        name={item.name}
        control={control}
        rules={{
          required: item.constraints?.required ? 'This field is required' : false
        }}
        render={({ fieldState }) => {
          return (
            <div data-testid="render-code-display">
              {/* Render HTML content dengan event delegation */}
              <div 
                ref={containerRef}
              >
                {htmlContent ? (
                  <div 
                    className="prose max-w-none"
                    dangerouslySetInnerHTML={memoizedHtml}
                  />
                ) : (
                  <p className="text-gray-500 italic">No HTML content to display</p>
                )}
              </div>
              
              {fieldState.error && (
                <p className="text-sm text-red-500 mt-1">{fieldState.error.message}</p>
              )}
              
            </div>
          );
        }}
      />
    </div>
  );
};
